class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      
      // ⚠️ Ignorer les changements d'URL liés à la pagination custom
      const url = new URL(window.location.href);
      if (url.searchParams.has('current-page')) {
        return;
      }
      
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    // ⚠️ Réinitialiser la pagination après le rendu Shopify
    resetPagination();
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };
    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);
        if (newElementToActivate) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    const mobileFacetsCountEl = document.querySelector('.mobile-facets__count');
    if (mobileFacetsCountEl) {
      const mobileFacetsCount = mobileFacetsCountEl.innerHTML;
      const mobileFacetsCountFirstLetter = mobileFacetsCount.charAt(0);

      const resultFilters = document.querySelector('[data-result-filters]');
      if (resultFilters) {
        resultFilters.innerHTML = ' ' + mobileFacetsCountFirstLetter;
        resultFilters.classList.add('active');
      }
    }

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileForm) {
      const menuDrawer = mobileForm.closest('menu-drawer');
      if (menuDrawer) menuDrawer.bindEvents();
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();


class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);


class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);


// ============================================
// PAGINATION CUSTOM - CODE CORRIGÉ
// ============================================

const PaginationManager = {
  currentPage: 1,
  isLoading: false,
  initialized: false,

  init() {
    if (!document.querySelector("[data-total-count]")) {
      return;
    }

    this.updateButtonsVisibility();
    this.restorePageFromUrl();
    this.updateProgressBar();

    if (!this.initialized) {
      this.setupEventDelegation();
      this.initialized = true;
    }
  },

  setupEventDelegation() {
    document.addEventListener('click', async (e) => {
      const loadMoreBtn = e.target.closest('.collection-show-more');
      const loadAllBtn = e.target.closest('.collection-show-all');

      if (loadMoreBtn) {
        e.preventDefault();
        e.stopPropagation();
        await this.loadMore();
      }

      if (loadAllBtn) {
        e.preventDefault();
        e.stopPropagation();
        await this.loadAll();
      }
    });
  },

  async loadMore() {
    if (this.isLoading) return;
    this.isLoading = true;
    this.currentPage++;

    try {
      const sectionId = document.getElementById('product-grid')?.dataset.id;

      // Build URL using current filters/sort but only change the page number
      const url = new URL(window.location.href);
      url.searchParams.delete('current-page');
      url.searchParams.delete('view');
      url.searchParams.set('page', this.currentPage);
      // Use section_id so Shopify returns only this section's HTML (no full page reload)
      if (sectionId) url.searchParams.set('section_id', sectionId);

      const response = await fetch(url);
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const newProducts = html.querySelectorAll('.grid__item');

      const productGrid = document.querySelector('#product-grid');
      if (productGrid && newProducts.length > 0) {
        newProducts.forEach((product) => {
          productGrid.appendChild(product);
        });
      }

      this.updateProgressBar();
      this.updateButtonsVisibility();

    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      this.currentPage--;
    } finally {
      this.isLoading = false;
    }
  },

  async loadAll() {
    if (this.isLoading) return;
    this.isLoading = true;

    const loadAllBtn = document.querySelector('.collection-show-all');
    if (loadAllBtn) loadAllBtn.classList.add('loading');

    try {
      const sectionId = document.getElementById('product-grid')?.dataset.id;
      let hasProducts = true;

      while (hasProducts) {
        this.currentPage++;

        const url = new URL(window.location.href);
        url.searchParams.delete('current-page');
        url.searchParams.delete('view');
        url.searchParams.set('page', this.currentPage);
        // Use section_id so Shopify returns only this section's HTML (no full page reload)
        if (sectionId) url.searchParams.set('section_id', sectionId);

        const response = await fetch(url);
        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newProducts = html.querySelectorAll('.grid__item');

        if (newProducts.length === 0) {
          hasProducts = false;
        } else {
          const productGrid = document.querySelector('#product-grid');
          if (productGrid) {
            newProducts.forEach((product) => {
              productGrid.appendChild(product);
            });
          }
        }

        this.updateProgressBar();
      }

      this.updateButtonsVisibility();
      // Use replaceState (not pushState) so popstate never fires → no filter reset
      this.updateUrlHistory('all');

    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      this.isLoading = false;
      if (loadAllBtn) loadAllBtn.classList.remove('loading');
    }
  },

  updateButtonsVisibility() {
    const currentCountEl = document.querySelector("[data-current-count]");
    const maxCountEl = document.querySelector("[data-total-count]");

    if (!currentCountEl || !maxCountEl) return;

    const currentCount = parseInt(currentCountEl.innerHTML) || 0;
    const maxCount = parseInt(maxCountEl.innerHTML) || 0;

    const buttons = document.querySelectorAll(".collection-show-all, .collection-show-more");

    if (currentCount >= maxCount) {
      buttons.forEach((el) => el.classList.add("hidden"));
    } else {
      buttons.forEach((el) => el.classList.remove("hidden"));
    }
  },

  restorePageFromUrl() {
    const url = new URL(window.location.href);
    const page = url.searchParams.get("current-page");

    if (!page) return;

    const showMoreBtn = document.querySelector(".collection-show-more");
    if (!showMoreBtn) return;

    const productPerPage = parseInt(showMoreBtn.dataset.loadMore) || 12;
    const products = document.querySelectorAll(".collection-filters__custom .grid__item");

    products.forEach((product, index) => {
      if (page === "all" || index < productPerPage * parseInt(page)) {
        product.classList.remove("hidden");
      }
    });

    if (page !== 'all') {
      this.currentPage = parseInt(page);
    }

    this.updateProgressBar();
  },

  updateUrlHistory(page) {
    const url = new URL(window.location.href);
    url.searchParams.set("current-page", page);
    // replaceState does NOT trigger popstate → no conflict with Shopify filters
    window.history.replaceState(null, "", url.href);
  },

  updateProgressBar() {
    const progressbarContainer = document.querySelector(".collection-progressbar");
    if (!progressbarContainer) return;

    const productShowed = document.querySelectorAll("main-collection .grid__item, #product-grid .grid__item").length;
    const totalCountEl = document.querySelector("[data-total-count]");
    if (!totalCountEl) return;

    const productTotal = parseInt(totalCountEl.innerHTML) || 1;
    const progress = Math.min((productShowed / productTotal) * 100, 100);

    document.documentElement.style.setProperty(
      "--collection-progress-bar-width",
      `${progress}%`
    );

    this.updatePaginationCount(productShowed);
  },

  updatePaginationCount(productShowed) {
    const paginationCount = document.querySelector("[data-current-count]");
    if (paginationCount && productShowed) {
      paginationCount.innerHTML = `${productShowed}`;
    }

    this.updateButtonsVisibility();
  },

  reset() {
    this.currentPage = 1;
    this.isLoading = false;
    const url = new URL(window.location.href);
    if (url.searchParams.has('current-page')) {
      url.searchParams.delete('current-page');
      window.history.replaceState(null, "", url.href);
    }
    this.init();
  }
};

// Called by Shopify after renderProductGridContainer
function resetPagination() {
  PaginationManager.reset();
}

// Compatibility with legacy code
function reloadPagination() {
  PaginationManager.init();
}

// Init
reloadPagination();